import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { DotcEnums } from 'src/common/contracts/enums/dotc'
import Checkbox from 'src/common/ui-kit/Checkbox/Checkbox'
import NumericField from 'src/common/ui-kit/NumericField/NumericField'
import TextField from 'src/common/ui-kit/TextField/TextField'
import QualifierSetting from './QualifierSetting'

export interface OfferOTCSettingsListProps {
  offerWithNft: boolean
  settings: OfferOTCSettings
  showOfferFillType?: boolean
  showTimelock?: boolean
  showQualifier?: boolean
  className?: string
  onChangeSettings: (settings: OfferOTCSettings) => void
}

export interface OfferOTCSettings {
  specialAddresses: string[]
  authorizationAddresses: string[]
  otherAuthorizationAddress: string | null
  timelockPeriod?: number
  commsLink: string
  terms: string
  takingOfferType?: DotcEnums.TakingOfferType
}

const OfferOTCSettingsList: React.FC<OfferOTCSettingsListProps> = ({
  settings,
  offerWithNft,
  showOfferFillType = true,
  showTimelock = true,
  showQualifier = true,
  className,
  onChangeSettings,
}) => {
  const { t } = useTranslation('common')

  const {
    specialAddresses = [],
    timelockPeriod,
    commsLink,
    terms,
    authorizationAddresses = [],
    otherAuthorizationAddress,
    takingOfferType,
  } = settings

  const specialAddress = specialAddresses[0] || ''

  const onChangePrivateAddress = (value: string) => {
    const updatedSettings = {
      ...settings,
      specialAddresses: [value, ...specialAddresses.slice(1)],
    }
    onChangeSettings(updatedSettings)
  }

  const onChangeTimelock = (value: number) => {
    const updatedSettings: OfferOTCSettings = {
      ...settings,
      timelockPeriod: value,
    }
    onChangeSettings(updatedSettings)
  }

  const onChangeTerms = (value: string) => {
    const updatedSettings: OfferOTCSettings = { ...settings, terms: value }
    onChangeSettings(updatedSettings)
  }

  const onChangeCommsLink = (value: string) => {
    const updatedSettings: OfferOTCSettings = { ...settings, commsLink: value }
    onChangeSettings(updatedSettings)
  }

  const onChangeIsBlockOffer = (checked: boolean) => {
    const updatedSettings: OfferOTCSettings = {
      ...settings,
      takingOfferType:
        takingOfferType === undefined
          ? undefined
          : checked
          ? DotcEnums.TakingOfferType.BlockOffer
          : DotcEnums.TakingOfferType.PartialOffer,
    }
    onChangeSettings(updatedSettings)
  }

  const onChangeAuthAddresses = (authorizationAddresses: string[]) => {
    const updatedSettings: OfferOTCSettings = {
      ...settings,
      authorizationAddresses,
    }
    onChangeSettings(updatedSettings)
  }

  const onChangeOtherAuthAddress = (
    otherAuthorizationAddress: string | null
  ) => {
    const updatedSettings: OfferOTCSettings = {
      ...settings,
      otherAuthorizationAddress,
    }
    onChangeSettings(updatedSettings)
  }

  const isBlockOffer = takingOfferType === DotcEnums.TakingOfferType.BlockOffer

  return (
    <div className={clsx('grid grid-cols-12 gap-4 items-center', className)}>
      <div className='typo-label2 col-span-3'>
        {t('offerOTCSettingsList.privateOffer')}:
      </div>
      <TextField
        className='col-span-9'
        type='text'
        value={specialAddress}
        onChange={onChangePrivateAddress}
        hideError
        inputProps={{
          placeholder: t('offerOTCSettingsList.enterTakerAddress'),
        }}
      />
      {showTimelock && timelockPeriod !== undefined && (
        <>
          <div className='typo-label2 col-span-3'>
            {t('offerOTCSettingsList.timelock')}
          </div>
          <NumericField
            className='col-span-9'
            type='number'
            value={timelockPeriod}
            onChange={onChangeTimelock}
            hideError
            displayValue={!timelockPeriod ? '' : null}
            inputProps={{
              placeholder: t('offerOTCSettingsList.numberOfDaysToLock'),
            }}
          />
        </>
      )}
      <div className='typo-label2 col-span-3'>
        {t('offerOTCSettingsList.terms')}
      </div>
      <TextField
        className='col-span-9'
        type='text'
        value={terms}
        onChange={onChangeTerms}
        hideError
        inputProps={{
          placeholder: t('offerOTCSettingsList.httpsOrIpfsUrl'),
        }}
      />
      <div className='typo-label2 col-span-3'>
        {t('offerOTCSettingsList.contact')}
      </div>
      <TextField
        className='col-span-9'
        type='text'
        value={commsLink}
        onChange={onChangeCommsLink}
        hideError
        inputProps={{
          placeholder: t('offerOTCSettingsList.twitterTelegramEtc'),
        }}
      />
      {showOfferFillType && (
        <>
          <div className='typo-label2 col-span-3'>
            {t('offerOTCSettingsList.blockOrder')}
          </div>
          <Checkbox
            className='col-start-12 col-span-1 justify-self-end'
            checked={isBlockOffer}
            disabled={offerWithNft}
            onChange={onChangeIsBlockOffer}
          />
        </>
      )}
      {/* {showQualifier && (
        <QualifierSetting
          authorizationAddresses={authorizationAddresses}
          onChangeAuthAddresses={onChangeAuthAddresses}
          otherAuthAddress={otherAuthorizationAddress}
          onChangeOtherAuthAddress={onChangeOtherAuthAddress}
        />
      )} */}
    </div>
  )
}

export default OfferOTCSettingsList
